import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Lightbulb, ArrowRight, ArrowLeft, Wrench, Database, CheckCircle, 
  AlertTriangle, Star, Zap, Target, Layers, ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

// Project templates with material and tool suggestions
const PROJECT_TEMPLATES = [
  {
    id: 'cutting-board',
    name: 'Cutting Board',
    description: 'Kitchen cutting boards and butcher blocks',
    icon: '🪵',
    difficulty: 'Beginner',
    category: 'Kitchen',
    materials: ['Maple', 'Walnut', 'Cherry', 'Oak'],
    tools: ['Downcut End Mill', 'Surfacing / Flattening'],
    tips: [
      'Use food-safe hardwoods only',
      'Compression or downcut bits for clean edges',
      'Leave extra stock for flattening after glue-up'
    ],
    warnings: ['Avoid softwoods - they harbor bacteria']
  },
  {
    id: 'sign-making',
    name: 'Signs & Lettering',
    description: 'Wooden signs, house numbers, business signage',
    icon: '🪧',
    difficulty: 'Beginner',
    category: 'Decorative',
    materials: ['Pine', 'Cedar', 'MDF', 'Baltic Birch Plywood'],
    tools: ['V-Bit', 'Ball Nose End Mill', 'Downcut End Mill'],
    tips: [
      'V-bits create sharp letter edges',
      'Use ball nose for 3D relief carving',
      'MDF paints beautifully for painted signs'
    ],
    warnings: ['Cedar oils may affect paint adhesion']
  },
  {
    id: 'cabinet-making',
    name: 'Cabinets & Boxes',
    description: 'Kitchen cabinets, storage boxes, drawers',
    icon: '🗄️',
    difficulty: 'Intermediate',
    category: 'Furniture',
    materials: ['Baltic Birch Plywood', 'Melamine', 'MDF', 'Plywood'],
    tools: ['Compression Bit', 'Downcut End Mill', 'Straight Bit'],
    tips: [
      'Compression bits essential for clean plywood edges',
      'Use dogbone or t-bone for inside corners',
      'Account for material thickness in dado joints'
    ],
    warnings: ['Test fit joints before final assembly']
  },
  {
    id: 'inlay-work',
    name: 'Inlays & Marquetry',
    description: 'Decorative inlays, epoxy fills, contrasting wood patterns',
    icon: '🎨',
    difficulty: 'Advanced',
    category: 'Decorative',
    materials: ['Walnut', 'Maple', 'Purpleheart', 'Padauk', 'Ebony'],
    tools: ['V-Bit', 'Ball Nose End Mill', 'Tapered Ball Nose'],
    tips: [
      'Use contrasting wood colors for impact',
      'Leave 0.1mm extra depth for epoxy inlays',
      'Sharp V-bits for crisp pocket edges'
    ],
    warnings: ['Exotic wood dust can cause allergic reactions']
  },
  {
    id: 'pcb-milling',
    name: 'PCB Prototyping',
    description: 'Circuit board isolation routing and drilling',
    icon: '🔌',
    difficulty: 'Advanced',
    category: 'Electronics',
    materials: ['G10/FR4'],
    tools: ['V-Bit', 'Engraving Bit'],
    tips: [
      'Use 30-60° V-bits for trace isolation',
      'Keep RPM high and feeds moderate',
      'Use sacrificial board underneath'
    ],
    warnings: ['Fiberglass dust is hazardous - use extraction']
  },
  {
    id: 'acrylic-projects',
    name: 'Acrylic Projects',
    description: 'LED signs, display cases, awards, enclosures',
    icon: '💎',
    difficulty: 'Intermediate',
    category: 'Plastic',
    materials: ['Acrylic', 'Polycarbonate'],
    tools: ['O-Flute End Mill', 'Upcut End Mill'],
    tips: [
      'Single flute O-flute prevents melting',
      'Cast acrylic machines better than extruded',
      'Use air blast to clear chips'
    ],
    warnings: ['Maintain chipload to prevent melting']
  },
  {
    id: 'aluminum-parts',
    name: 'Aluminum Parts',
    description: 'Brackets, enclosures, machine parts',
    icon: '⚙️',
    difficulty: 'Advanced',
    category: 'Metal',
    materials: ['Aluminum'],
    tools: ['End Mill'],
    tips: [
      'Use cutting fluid or WD-40',
      'Lower RPM than wood - around 10-15k',
      'Ensure rigid workholding'
    ],
    warnings: ['High rigidity machine required', 'Chips can weld to tool without lubrication']
  },
  {
    id: 'cosplay-props',
    name: 'Cosplay & Props',
    description: 'Armor, weapons, costume pieces',
    icon: '🎭',
    difficulty: 'Beginner',
    category: 'Creative',
    materials: ['EVA Foam', 'Rigid Foam', 'EPS Foam', 'MDF'],
    tools: ['Ball Nose End Mill', 'End Mill'],
    tips: [
      'EVA foam is flexible and lightweight',
      'Use heat gun to shape foam after cutting',
      'MDF for rigid structural pieces'
    ],
    warnings: ['Foam dust is messy - use extraction']
  },
  {
    id: 'guitar-making',
    name: 'Musical Instruments',
    description: 'Guitar bodies, necks, fretboards',
    icon: '🎸',
    difficulty: 'Advanced',
    category: 'Woodworking',
    materials: ['Mahogany', 'Maple', 'Rosewood', 'Walnut', 'Ash'],
    tools: ['Ball Nose End Mill', 'Upcut End Mill', 'Compression Bit'],
    tips: [
      'Rosewood for fretboards (check CITES)',
      'Mahogany and ash popular for bodies',
      'Maple for necks - stability is key'
    ],
    warnings: ['Some rosewood species are CITES protected']
  },
  {
    id: 'jigs-fixtures',
    name: 'Jigs & Fixtures',
    description: 'Workholding, templates, shop fixtures',
    icon: '📐',
    difficulty: 'Beginner',
    category: 'Shop',
    materials: ['MDF', 'Baltic Birch Plywood', 'HDPE', 'Delrin/Acetal'],
    tools: ['Upcut End Mill', 'Straight Bit'],
    tips: [
      'MDF is cheap and dimensionally stable',
      'HDPE and Delrin for wear surfaces',
      'Baltic birch for precision fixtures'
    ],
    warnings: ['Replace MDF jigs when worn']
  }
];

const QuickPick = () => {
  const { token } = useContext(AuthContext);
  const [selectedProject, setSelectedProject] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [tools, setTools] = useState([]);
  const [matchedMaterials, setMatchedMaterials] = useState([]);
  const [matchedTools, setMatchedTools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedProject && materials.length > 0 && tools.length > 0) {
      matchResources();
    }
  }, [selectedProject, materials, tools]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [materialsRes, toolsRes] = await Promise.all([
        axios.get(`${API}/materials`),
        axios.get(`${API}/tools-v2`, { headers })
      ]);
      setMaterials(materialsRes.data);
      setTools(toolsRes.data.tools || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setTools([]);
    } finally {
      setIsLoading(false);
    }
  };

  const matchResources = () => {
    if (!selectedProject) return;

    // Match materials by name
    const matched = materials.filter(m => 
      m && m.name && selectedProject.materials.some(pm => 
        m.name.toLowerCase().includes(pm.toLowerCase()) ||
        pm.toLowerCase().includes(m.name.toLowerCase())
      )
    );
    setMatchedMaterials(matched);

    // Match tools by category/name
    const matchedT = tools.filter(t =>
      t && t.name && selectedProject.tools.some(pt =>
        t.name.toLowerCase().includes(pt.toLowerCase()) ||
        (t.category && t.category.toLowerCase().includes(pt.toLowerCase())) ||
        pt.toLowerCase().includes(t.name.toLowerCase())
      )
    );
    setMatchedTools(matchedT);
  };

  const categories = [...new Set(PROJECT_TEMPLATES.map(p => p.category))];
  const filteredProjects = categoryFilter === 'all' 
    ? PROJECT_TEMPLATES 
    : PROJECT_TEMPLATES.filter(p => p.category === categoryFilter);

  const difficultyColors = {
    'Beginner': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Intermediate': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'Advanced': 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  if (selectedProject) {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
        style={bgStyle(IMAGES.FEEDS_SPEEDS)}
        data-testid="quick-pick-detail"
      >
        <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />

        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => setSelectedProject(null)}
          className="mb-4 text-[#d6d6d6] hover:text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>

        {/* Project Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-5xl">{selectedProject.icon}</span>
            <div>
              <h1 className="text-3xl font-bold text-white">{selectedProject.name}</h1>
              <p className="text-[#a3a3a3]">{selectedProject.description}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className={difficultyColors[selectedProject.difficulty]}>
              {selectedProject.difficulty}
            </Badge>
            <Badge className="bg-[#e67e22]/20 text-[#e67e22] border-[#e67e22]/30">
              {selectedProject.category}
            </Badge>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recommended Materials */}
          <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Database className="w-5 h-5 text-[#e67e22]" />
                Recommended Materials
              </CardTitle>
              <CardDescription className="text-[#a3a3a3]">
                Best material choices for this project type
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {matchedMaterials.length > 0 ? (
                matchedMaterials.map(material => (
                  <div 
                    key={material.id}
                    className="p-3 bg-[#1f1f1f] border border-[#3a3a3a] rounded-sm hover:border-[#e67e22]/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-white">{material.name}</div>
                        <div className="text-xs text-[#a3a3a3]">{material.category} • {material.subcategory}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-500/20 text-green-400 text-xs">
                          <Star className="w-3 h-3 mr-1" />
                          Recommended
                        </Badge>
                        <Link to={`/tools/material-db`}>
                          <Button size="sm" variant="ghost" className="text-[#e67e22]">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs text-[#d6d6d6] border-[#3a3a3a]">
                        Hardness: {material.hardness}
                      </Badge>
                      <Badge variant="outline" className="text-xs text-[#d6d6d6] border-[#3a3a3a]">
                        {material.machinability} to machine
                      </Badge>
                    </div>
                  </div>
                ))
              ) : isLoading ? (
                <div className="text-center py-6 text-[#a3a3a3]">
                  <p>Loading materials...</p>
                </div>
              ) : (
                <div className="text-center py-6 text-[#a3a3a3]">
                  <p>No matching materials found in database</p>
                </div>
              )}

              {/* Other suggested materials not in DB */}
              {selectedProject.materials.filter(pm => 
                !matchedMaterials.some(m => 
                  m.name.toLowerCase().includes(pm.toLowerCase())
                )
              ).length > 0 && (
                <div className="pt-3 border-t border-[#3a3a3a]">
                  <div className="text-xs text-[#a3a3a3] mb-2">Also consider:</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.materials.filter(pm => 
                      !matchedMaterials.some(m => 
                        m.name.toLowerCase().includes(pm.toLowerCase())
                      )
                    ).map(pm => (
                      <Badge key={pm} variant="outline" className="text-[#d6d6d6] border-[#3a3a3a]">
                        {pm}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recommended Tools */}
          <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wrench className="w-5 h-5 text-[#e67e22]" />
                Recommended Tools
              </CardTitle>
              <CardDescription className="text-[#a3a3a3]">
                Best cutting tools for this project type
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {matchedTools.length > 0 ? (
                matchedTools.map(tool => (
                  <div 
                    key={tool.id}
                    className="p-3 bg-[#1f1f1f] border border-[#3a3a3a] rounded-sm hover:border-[#e67e22]/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-white">{tool.name}</div>
                        <div className="text-xs text-[#a3a3a3]">{tool.category}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-500/20 text-green-400 text-xs">
                          <Star className="w-3 h-3 mr-1" />
                          Recommended
                        </Badge>
                        <Link to={`/tools/bit-selector`}>
                          <Button size="sm" variant="ghost" className="text-[#e67e22]">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                    {tool.recommended_chipload && (
                      <div className="mt-2 text-xs text-[#a3a3a3]">
                        Chipload: {tool.recommended_chipload.min}-{tool.recommended_chipload.max} mm/tooth
                      </div>
                    )}
                  </div>
                ))
              ) : isLoading ? (
                <div className="text-center py-6 text-[#a3a3a3]">
                  <p>Loading tools...</p>
                </div>
              ) : (
                <div className="text-center py-6 text-[#a3a3a3]">
                  <p>No matching tools found in database</p>
                </div>
              )}

              {/* Other suggested tools not in DB */}
              {selectedProject.tools.filter(pt => 
                !matchedTools.some(t => 
                  t.name.toLowerCase().includes(pt.toLowerCase()) ||
                  (t.category && t.category.toLowerCase().includes(pt.toLowerCase()))
                )
              ).length > 0 && (
                <div className="pt-3 border-t border-[#3a3a3a]">
                  <div className="text-xs text-[#a3a3a3] mb-2">Tool types to look for:</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tools.filter(pt => 
                      !matchedTools.some(t => 
                        t.name.toLowerCase().includes(pt.toLowerCase()) ||
                        (t.category && t.category.toLowerCase().includes(pt.toLowerCase()))
                      )
                    ).map(pt => (
                      <Badge key={pt} variant="outline" className="text-[#d6d6d6] border-[#3a3a3a]">
                        {pt}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="bg-black/60 backdrop-blur-sm border-green-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Pro Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {selectedProject.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#d6d6d6]">
                    <Zap className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{tip}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Warnings */}
          {selectedProject.warnings.length > 0 && (
            <Card className="bg-black/60 backdrop-blur-sm border-amber-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Watch Out
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {selectedProject.warnings.map((warning, i) => (
                    <li key={i} className="flex items-start gap-2 text-amber-200/80">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{warning}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/tools/feeds-speeds">
            <Button className="bg-[#e67e22] hover:bg-[#d35400] text-white">
              Open Calculator
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/tools/bit-selector">
            <Button variant="outline" className="border-[#3a3a3a] text-white hover:bg-white/10">
              Browse All Tools
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/tools/material-db">
            <Button variant="outline" className="border-[#3a3a3a] text-white hover:bg-white/10">
              Browse All Materials
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.FEEDS_SPEEDS)}
      data-testid="quick-pick-page"
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Back to Tools */}
      <Link to="/tools">
        <Button
          variant="ghost"
          className="mb-4 text-[#d6d6d6] hover:text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tools
        </Button>
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Lightbulb className="w-8 h-8 text-[#e67e22]" />
          <h1 className="text-3xl font-bold text-white">Project Picker</h1>
        </div>
        <p className="text-[#a3a3a3] max-w-2xl">
          Select your project type to get instant recommendations for the best materials and tools. 
          Perfect for getting started quickly!
        </p>
      </div>

      {/* Category Filter */}
      <div className="mb-6 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={categoryFilter === 'all' ? 'default' : 'outline'}
          onClick={() => setCategoryFilter('all')}
          className={categoryFilter === 'all' 
            ? 'bg-[#e67e22] hover:bg-[#d35400] text-white' 
            : 'border-[#3a3a3a] text-[#d6d6d6] hover:bg-white/10'
          }
        >
          All Projects
        </Button>
        {categories.map(cat => (
          <Button
            key={cat}
            size="sm"
            variant={categoryFilter === cat ? 'default' : 'outline'}
            onClick={() => setCategoryFilter(cat)}
            className={categoryFilter === cat 
              ? 'bg-[#e67e22] hover:bg-[#d35400] text-white' 
              : 'border-[#3a3a3a] text-[#d6d6d6] hover:bg-white/10'
            }
          >
            {cat}
          </Button>
        ))}
      </div>

      {/* Project Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProjects.map(project => (
          <Card 
            key={project.id}
            className="bg-black/60 backdrop-blur-sm border-[#3a3a3a] hover:border-[#e67e22]/50 cursor-pointer transition-all hover:shadow-[0_0_20px_rgba(230,126,34,0.15)]"
            onClick={() => setSelectedProject(project)}
            data-testid={`project-card-${project.id}`}
          >
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <span className="text-4xl">{project.icon}</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-white text-lg">{project.name}</h3>
                  <p className="text-sm text-[#a3a3a3] mt-1">{project.description}</p>
                </div>
              </div>
              
              <div className="mt-4 flex gap-2 flex-wrap">
                <Badge className={difficultyColors[project.difficulty]}>
                  {project.difficulty}
                </Badge>
                <Badge variant="outline" className="text-[#a3a3a3] border-[#3a3a3a]">
                  {project.materials.length} materials
                </Badge>
                <Badge variant="outline" className="text-[#a3a3a3] border-[#3a3a3a]">
                  {project.tools.length} tools
                </Badge>
              </div>

              <div className="mt-4 pt-4 border-t border-[#3a3a3a]">
                <div className="text-xs text-[#737373] mb-2">Top materials:</div>
                <div className="flex flex-wrap gap-1">
                  {project.materials.slice(0, 3).map(m => (
                    <Badge key={m} variant="outline" className="text-xs text-[#d6d6d6] border-[#3a3a3a]">
                      {m}
                    </Badge>
                  ))}
                  {project.materials.length > 3 && (
                    <Badge variant="outline" className="text-xs text-[#737373] border-[#3a3a3a]">
                      +{project.materials.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>

              <Button 
                className="w-full mt-4 bg-[#1f1f1f] hover:bg-[#2c2c2c] text-[#e67e22] border border-[#3a3a3a]"
                variant="outline"
              >
                View Recommendations
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Help Text */}
      <div className="mt-8 text-center">
        <p className="text-[#737373] text-sm">
          Don&apos;t see your project? Browse the{' '}
          <Link to="/tools/material-db" className="text-[#e67e22] hover:underline">Material Database</Link>
          {' '}or{' '}
          <Link to="/tools/bit-selector" className="text-[#e67e22] hover:underline">Bit Selector</Link>
          {' '}directly.
        </p>
      </div>
    </div>
  );
};

export default QuickPick;
